import * as Sentry from '@sentry/react';
import React from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';

// Sentry configuration
export const initSentry = () => {
  // Only initialize Sentry if DSN is provided
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.log('Sentry DSN not provided, skipping Sentry initialization');
    return;
  }

  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      integrations: [
        Sentry.browserTracingIntegration({
          // Set up automatic route change tracking for React Router
          routingInstrumentation: Sentry.reactRouterV6Instrumentation(
            React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes
          ),
        }),
      ],
      
      // Performance monitoring
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      
      // Session replay for debugging
      replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,
      
      // Error filtering
      beforeSend(event, hint) {
        // Filter out known non-critical errors
        const error = hint.originalException;
        
        if (error && typeof error === 'object' && 'message' in error) {
          const message = error.message as string;
          
          // Filter out network errors that are expected
          if (message.includes('Failed to fetch') || 
              message.includes('NetworkError') ||
              message.includes('Load failed')) {
            return null;
          }
          
          // Filter out browser extension errors
          if (message.includes('Extension context invalidated') ||
              message.includes('chrome-extension://')) {
            return null;
          }
        }
        
        return event;
      },
      
      // Additional context
      initialScope: {
        tags: {
          component: 'aegis-vault-frontend'
        }
      }
    });
    
    console.log('Sentry initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

// Custom error boundary component
export const SentryErrorBoundary = Sentry.withErrorBoundary;

// Custom hooks for error tracking
export const useSentryUser = (user: any) => {
  React.useEffect(() => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.name,
      });
    }
  }, [user]);
};

export const useSentryBreadcrumb = (category: string, message: string, data?: any) => {
  React.useEffect(() => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.addBreadcrumb({
        category,
        message,
        data,
        level: 'info',
      });
    }
  }, [category, message, data]);
};

// Error tracking utilities
export const trackError = (error: Error, context?: Record<string, any>) => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach(key => {
        scope.setContext(key, context[key]);
      });
    }
    Sentry.captureException(error);
  });
};

export const trackEvent = (event: string, data?: Record<string, any>) => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  Sentry.addBreadcrumb({
    category: 'user-action',
    message: event,
    data,
    level: 'info',
  });
};

// Performance monitoring
export const startTransaction = (name: string, op: string) => {
  if (!import.meta.env.VITE_SENTRY_DSN) return null;
  
  return Sentry.startTransaction({ name, op });
};